import React from 'react';
import moment from 'moment';
import './App.css';
import { useTranslation } from 'react-i18next';
import { Packet } from './api/Models';
import { Button, Field, Input, Spinner } from '@fluentui/react-components';
import { RestClient } from './api/RestClient';
import PaypalButtons from './PaypalButtons';

export interface PacketBuyProps {
  packet: Packet;
  packetBuyed: (paylink?: string) => Promise<void>;
  back: () => void;
}
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function PacketBuy({packet, packetBuyed, back}: PacketBuyProps) {
  const [email, setEmail] = React.useState<string | undefined>();
  const [emailError, setEmailError] = React.useState<string | undefined>();
  const [confirmState, setConfirmState] = React.useState<boolean>(false);
  const [confirmCode, setConfirmCode] = React.useState<string | undefined>();
  const [confirmCodeError, setConfrimCodeError] = React.useState<string | undefined>();
  const [orderId, setOrderId] = React.useState<string | undefined>();
  const [waitSpinner, setWaitSpinner] = React.useState(false);
  const [payLink, setPayLink] = React.useState<string | undefined>();
  const [paypalCliendID, setPaypalCliendID] = React.useState<string | undefined>();
  const [pbCountry, setPbCountry] = React.useState<string | undefined>();
  const [paypalCurrency, setPaypalCurrency] = React.useState<string | undefined>();
  const [pageError, setPageError] = React.useState<string | undefined>();
  const [showPayBtn, setShowPayBtn] = React.useState(false);
  const { t, i18n } = useTranslation();

  const confirm = async (useLink: boolean) => {
    if (confirmState) {
      if (!confirmCode || confirmCode.trim().length < 6) {
        setConfrimCodeError(t('errInvalidCode'));
      } else {
        setConfrimCodeError(undefined);
        setWaitSpinner(true);
        // Devo inviare la richiesta di conferma al server
        try {
          await RestClient.getInstance().confirmOrderPacket(packet.id, orderId||'', confirmCode,i18n.language, false, useLink?payLink:undefined);
          if (useLink) {
            packetBuyed(payLink);
          } else {
            packetBuyed();
          }
        } catch(err) {
          console.log(err);
          setConfrimCodeError(t('errCheckCode'));
        } finally {
          setWaitSpinner(false);
        }
      }
    } else {
      if (!email || !emailPattern.test(email)) {
        setEmailError(t('errEmailRequired'));
        return;
      } else {
        // Invio la mail e il packet id per avviare il processo di vendita
        // In risposta disabilito la mail e visualizzo il campo per l'inserimento del codice di conferma
        setEmailError(undefined);
        setWaitSpinner(true);
        try {
          const oId = await RestClient.getInstance().orderPacket(packet.id, email,i18n.language);
          setOrderId(oId.orderID);
          setPayLink(oId.paylink);
          setPaypalCliendID(oId.paypal);
          setPbCountry(oId.pbCountry);
          setPaypalCurrency(oId.paypalCurrency);
          setConfirmState(true);
        } catch(err) {
          console.log(err);
          setEmailError(t('errOrderCreate'));
        } finally {
          setWaitSpinner(false);
        }
      }
    }
  }

  const cancel = async () => {
    if (confirmState) {
      setConfirmCode(undefined);
      setConfrimCodeError(undefined);
      setConfirmState(false);
      setShowPayBtn(false);
    }

    back();    
  }
  const imgSrc = (process.env.REACT_APP_API_BASE_URL || '')+'/sale/imgth/'+packet.id+'/'+packet.firstimage;

  const packetDateTime = (packet && packet.ts)?moment(packet.ts, "YYYY-MM-DD hh:mm:ss").format(t('packteDateTimeFormat')):'';

  return (
    <div style={{display: 'flex', flexDirection: 'column'}}>
      <div style={{flex: '1 1 0', display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '10px'}}>
        <img src={imgSrc} style={{maxHeight:  '150px', maxWidth: '150px'}} alt={packet.ts}/>
      </div>
      <div className='pktDet'>
        <p style={{marginBlockStart: '4px', marginBlockEnd: '4px'}}>{t('photosTakenLbl')} {packetDateTime}</p><p style={{marginBlockStart: '4px', marginBlockEnd: '4px'}}>{t('priceLbl')} {packet.amount} {t('currencyLbl')}</p>
      </div>
      <div style={{flex: '1 1 0', display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '10px'}}>
        <Field
          style={{width: '100%', maxWidth: '400px'}}
          label={t('emailLbl')}
          required={!confirmState}
          validationMessage={emailError}
        >
          <Input type='email'
            value={email}
            onChange={(ev, data) => setEmail(data.value)}
            disabled={confirmState || waitSpinner}
          />
        </Field>
      </div>
      {!confirmState &&
      <div style={{flex: '1 1 0', display: 'flex', gap: '20px', flexDirection: 'row', justifyContent: 'center', padding: '10px'}}>
        <p>{t('orderSetEmailMsg')}</p>
      </div>
      }
      {confirmState &&
        <div style={{flex: '1 1 0', display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '10px'}}>
          <Field
            style={{width: '100%', maxWidth: '400px'}}
            label={t('confirmCodeLbl')}
            required={true}
            validationMessage={confirmCodeError}
          >
            <Input type='text'
              value={confirmCode}
              onChange={(ev, data) => {setConfirmCode(data.value); if (data.value && data.value.trim().length >= 6) {setShowPayBtn(true);}}}
              disabled={waitSpinner}
            />
          </Field>
        </div>
      }
      {confirmState &&
      <div style={{flex: '1 1 0', display: 'flex', gap: '20px', flexDirection: 'row', justifyContent: 'center', padding: '10px'}}>
        <p>{t('orderSetCodeMsg')}</p>
      </div>
      }
      {pageError &&
      <div style={{flex: '1 1 0', display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '4px'}}>
        <p className='pTitle'>{pageError}</p>
      </div>
      }
      <div style={{flex: '1 1 0', display: 'flex', gap: '5px', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: '10px'}}>
        {(!confirmState) &&
          <Button style={{maxHeight: 55}} appearance="primary" onClick={() => confirm(false)} disabled={waitSpinner}>{t(confirmState?'confirmTransferBtn':'confirmBtn')}</Button>
        }
        {(paypalCliendID && confirmState && showPayBtn) &&
          // Devo aggiungere il pulsante paga con paypall
          <PaypalButtons back={packetBuyed} showError={setPageError} clientID={paypalCliendID} confirmCode={confirmCode || ''} orderID={orderId || ''} packetID={packet.id} buyerCountry={pbCountry} currency={paypalCurrency || ''}/>
        }
        {(payLink && confirmState && showPayBtn) &&
          <Button style={{maxHeight: 55}} appearance="primary" onClick={() => confirm(true)} disabled={waitSpinner}>{t('confirmLinkBtn')}</Button>
        }
        <Button style={{maxHeight: 55}} appearance="secondary" onClick={cancel} disabled={waitSpinner}>{t('cancelBtn')}</Button>
        {waitSpinner && <Spinner/>}
      </div>
    </div>
  );
}

export default PacketBuy;
